import { c as _c } from "react/compiler-runtime";
import { dbFormSubset, OnChangeProps, OnUpdateProps, useDbForm } from 'components/hooks/useDbForm';
import { usePortal } from '../portal/portal';
import { concat } from 'shared/collection';
import { doSafe, doTo } from 'shared/promise';
import { useMemoArray, useMemoValue } from 'shared/ui';
import { z, ZodObject, ZodSchema, ZodType, ZodTypeDef } from 'zod';
import { useQuerySbSync, UseQuerySbSyncProps } from 'utils/db/useQuerySb';
import { NotNull, PlainObject, zx } from 'shared/type';
import deepEquals from 'fast-deep-equal';
import { chunk, indexBy, last, partition } from 'remeda';
import { match } from 'ts-pattern';
import { DateTime } from 'luxon';
import { useLatest } from 'shared/ui/useLatest';
import { useCallback, useEffect, useMemo } from 'react';
import { nanoid } from 'nanoid';
import { FormErrors, zodResolver } from '@mantine/form';
import { asyncMap } from 'modern-async';
import { fmtSchema } from 'utils/fmtSchema';
const toStateRow = ({
  path,
  sub_path,
  ...r
}: UIStateRow): UIStateWithPath => ({
  ...r,
  path: path.split('/'),
  sub_path: sub_path.split('/')
});
export const zUIStateRow = z.object({
  /** natural id,  */
  id: z.string(),
  cfg: z.object({}).passthrough(),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  workspace_id: z.string(),
  type: z.string(),
  /** the path for filtering, does not includ subPath e.g.  feedback */
  path: z.string(),
  /** the sub-path to address the exact item. does not include path e.g. xxx */
  sub_path: z.string().default(''),
  /** the full path to this item. derived from other columns i.e. {created_by/workspace_id}|{path}/{subPath}  */
  load_path: z.string().optional(),
  deleted: z.boolean().default(false) // not in user ui, but in workspace
});
type UIStateRow = z.infer<typeof zUIStateRow>;
type UIStateWithPath = Omit<UIStateRow, 'path' | 'sub_path'> & {
  path: string[];
  sub_path: string[];
};
// const zUIRowSelected = zUiRow.pick({id: true, cfg: true, created_by: true, updated_at: true, deleted: true, type: true, workspace_id: true, path:true})

export type UIStateProps<TZodShape extends typeof zUIStateRow.shape = typeof zUIStateRow.shape> = {
  mode?: 'user' | 'workspace';
  path: string[];
  queryProps?: Partial<UseQuerySbSyncProps<TZodShape>>;
  filter?: {
    types?: string[];
    subPath?: string[];
  };
  debugName?: string;
  disabled?: boolean;
};

/** All the temp UI state for this user under a path (follows app url paths as close as possible) */
const useUIStateRaw = <TZodShape extends typeof zUIStateRow.shape = typeof zUIStateRow.shape,>(props: UIStateProps<TZodShape>) => {
  const {
    mode = 'user',
    path,
    queryProps,
    filter,
    debugName,
    disabled
  } = props;
  const {
    profile
  } = usePortal();
  const {
    workspace_id,
    id: created_by
  } = profile;
  const schema = (queryProps?.schema ?? zUIStateRow) as ZodObject<TZodShape>;
  const query = useQuerySbSync<TZodShape>({
    schema,
    ...match(mode).with('user', () => ({
      table: 'ui_user' as const,
      filter: {
        load_path: `${profile.id}|${path.join('/')}`
      }
    })).with('workspace', () => ({
      table: 'ui_workspace' as const,
      filter: {
        load_path: `${workspace_id}|${path.join('/')}`,
        deleted: false
      }
    })).exhaustive(),
    ...queryProps,
    debugName,
    clientFilter: useCallback((r: z.infer<ZodObject<TZodShape>>) => {
      const typesOk = filter?.types == null ? true : r.type ? filter.types.includes(r.type) : false;
      const subPathOk = filter?.subPath == null ? true : filter.subPath.join('/') == r.sub_path;
      return typesOk && subPathOk;
    }, [JSON.stringify(filter)]),
    disableChannel: mode == 'user' || disabled,
    options: {
      enabled: !disabled
    }
  });
  const dataFilteredRaw = useMemo(() => query.data?.filter(d => filter?.types == null ? true : d.type && filter.types.includes(d.type)), [query.data]);
  const dataFiltered = useMemoArray(dataFilteredRaw); // ignore changes if outside our filter according to item references
  const {
    byId: byId_0,
    data: data_0
  } = useMemo(() => {
    // client side filter the query caching
    const data = dataFiltered?.map(d_0 => toStateRow(d_0 as UIStateRow));
    const byId = data ? indexBy(data, d_1 => d_1.id) : undefined;
    return {
      data,
      byId
    };
  }, [dataFiltered]);

  /** lists of the the given type and descendant of the given subPath */
  const list = <Z extends ZodSchema<any>,>({
    schema: schema_0,
    type: typeRaw,
    subPath
  }: {
    schema: Z;
    type?: string;
    subPath?: string[];
  }) => {
    const type = typeRaw ?? schema_0.description;
    const listPath = concat(path, subPath);
    type T = z.infer<Z>;
    return data_0?.filter(r_0 => r_0.type == type && withPathFn(listPath).isDescendant(concat(r_0.path, r_0.sub_path))).map(({
      cfg,
      id,
      ...r_1
    }) => ({
      ...r_1,
      cfg: schema_0.parse(cfg) as T,
      itemId: last(r_1.sub_path)!
    }));
  };

  /** gives you crud operations for a single object of a given type. if you are using in combo with a form consider `useUIItem` */
  const crudItem = <Z extends ZodSchema<any>,>({
    schema: schema_1,
    type: typeRaw_0,
    subPath: subPath_0 = []
  }: {
    schema: Z;
    type?: string;
    subPath?: string[];
  }) => {
    // use the natural key to look up, and the id for updating the db
    const fullPath = concat(path, subPath_0).join('/');
    const type_0 = typeRaw_0 ?? schema_1.description;
    // derived in the db, ensure it is the same
    const id_0 = match(mode).with('user', () => `${created_by}|${type_0}|${fullPath}`).with('workspace', () => `${workspace_id}|${type_0}|${fullPath}`).exhaustive();
    type T = z.infer<Z>;
    const item = doSafe(byId_0?.[id_0], d_2 => {
      const parseRes = schema_1.safeParse(d_2.cfg);
      if (!parseRes.success) {
        console.error('error parsing cfg', debugName, {
          error: parseRes.error,
          schema: fmtSchema(schema_1),
          cfg: d_2.cfg,
          description: schema_1.description
        });
        return undefined;
      }
      return {
        ...d_2,
        cfg: parseRes.data as T
      };
    });
    const upsert = async (cfg_0: T) => query.upsert({
      cfg: cfg_0,
      workspace_id,
      created_by,
      type: type_0,
      path: path.join('/'),
      sub_path: subPath_0.join('/'),
      updated_at: DateTime.utc().toISO()!
    } as any);
    const del = async () => {
      match(mode).with('user', () => query.del(id_0)) // user state is more ephemeral
      .with('workspace', () => query.update({
        id: id_0,
        deleted: true,
        updated_at: DateTime.utc().toISO()!
      } as any)); // workspace is more solid and we may want to get it back
    };
    return {
      item,
      upsert,
      del
    };
  };
  const {
    isPending,
    isLoading
  } = query;
  return {
    mode,
    data: data_0,
    path,
    workspace_id,
    created_by,
    crudItem,
    list,
    isPending,
    isLoading,
    props,
    query
  };
};
export const useUIState = props => {
  const $ = _c(5);
  const state = useUIStateRaw(props);
  let t0;
  if ($[0] !== state) {
    t0 = props_0 => getCrudList({
      ...props_0,
      state
    });
    $[0] = state;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const crudList = t0;
  let t1;
  if ($[2] !== crudList || $[3] !== state) {
    t1 = {
      ...state,
      crudList
    };
    $[2] = crudList;
    $[3] = state;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export type UIStateListItem<T = {}> = Omit<UIStateWithPath, 'cfg'> & {
  cfg: T;
  itemId: string;
};
type UIStateRaw<ZState extends typeof zUIStateRow.shape = typeof zUIStateRow.shape> = ReturnType<typeof useUIStateRaw<ZState>>;
export type UIState = ReturnType<typeof useUIState>;
export type UIStateItem = NotNull<UIState['data']>[number];
type CrudListProps<Z extends ZodSchema<any>> = {
  schema: Z;
  type?: string;
  subPath?: string[];
};
export const getCrudList = <Z extends ZodSchema<any>, ZState extends typeof zUIStateRow.shape = typeof zUIStateRow.shape>({
  state,
  schema,
  type: typeRaw,
  subPath
}: CrudListProps<Z> & {
  state: UIStateRaw<ZState>;
}) => {
  const {
    workspace_id,
    created_by,
    path,
    query,
    mode
  } = state;
  type T = z.infer<Z>;
  const listPath = concat(state.path, subPath);
  const type = typeRaw ?? schema.description;
  const toListItem = ({
    cfg,
    id,
    ...r
  }: UIStateWithPath): UIStateListItem<T> => ({
    ...r,
    cfg: schema.parse(cfg) as T,
    id: id!,
    itemId: last(r.sub_path ?? [])!
  });
  const items: UIStateListItem<T>[] | undefined = state.data?.filter(r => r.type == type && withPathFn(listPath).isChild(concat(r.path, r.sub_path))).map(toListItem);
  const getId = (itemId: string) => `${match(state.mode).with('user', () => created_by).with('workspace', () => workspace_id).exhaustive()}|${type}|${concat(listPath, itemId).join('/')}`;
  const upsert = async ({
    cfg,
    itemId: itemIdRaw
  }: {
    cfg: T['cfg'];
    itemId?: string;
  }) => {
    const itemId = itemIdRaw ?? nanoid(6);
    const {
      data,
      ...res
    } = await query.upsert({
      cfg,
      workspace_id,
      created_by,
      type,
      path: path.join('/'),
      sub_path: concat(subPath, itemId).join('/')
    } as any);
    return {
      ...res,
      data: data ? toListItem(toStateRow(data as any)) : undefined
    };
  };
  const del = async (itemId: string) => {
    const id = getId(itemId);
    await match(mode).with('user', () => query.del(id)) // user state is more ephemeral
    .with('workspace', () => query.update({
      id,
      deleted: true,
      updated_at: DateTime.utc().toISO()!
    } as any)); // workspace is more solid and we may want to get it back
  };
  const delAll = async () => {
    const ids = items?.map(i => getId(i.itemId));
    // batch into what will fit into the query string https://github.com/supabase/postgrest-js/issues/393
    if (ids) await asyncMap(chunk(ids ?? [], 20), async ids => {
      await match(mode).with('user', () => query.del(ids)).with('workspace', () => query.updateAll({
        ids,
        update: {
          deleted: true
        } as any
      }));
    }, 4);
  };
  return {
    items,
    upsert,
    del,
    delAll,
    subPath
  };
};
const withPathFn = (path: string[]) => ({
  /** is `p` a child of this path */
  isChild: (p: string[]) => p.length == path.length + 1 && path.every((s, i) => p[i] == s),
  isDescendant: (p: string[]) => p.length > path.length && path.every((s, i) => p[i] == s)
});
export const hasUpdatedAt = (o: object | null | undefined): o is {
  updated_at: string;
} => o != null && 'updated_at' in o && o.updated_at != null;
const {
  debug: consoleDebug
} = console;
export const listMods = <T extends {
  id: string;
},>(data: T[], options?: {
  naturalKeyCol?: keyof T;
  schema?: ZodSchema<T>;
  debugName?: string;
}) => {
  const {
    naturalKeyCol,
    schema,
    debugName
  } = options ?? {};
  const del = (id: string) => data.filter(d => d.id != id);
  const upsert = (newRow: Partial<T> & {
    id?: string;
  }) => {
    const naturalKey = naturalKeyCol ? newRow[naturalKeyCol] : undefined;
    if (newRow.id == null && !naturalKey) return data;
    const existingIndex = data.findIndex(r => newRow.id == null ? naturalKeyCol && r[naturalKeyCol] == naturalKey : r.id == newRow.id);
    const item = existingIndex !== -1 ? data[existingIndex] : undefined;
    const isPastUpdate = hasUpdatedAt(item) && hasUpdatedAt(newRow) && DateTime.fromISO(item.updated_at).toSeconds() >= DateTime.fromISO(newRow.updated_at).toSeconds();
    const toUpsertRaw = {
      ...item,
      ...newRow
    } as T;
    const isSameValues = item && deepEquals(item, toUpsertRaw);
    const toUpsert = doSafe(schema, s => {
      const parseRes = s.safeParse(toUpsertRaw);
      if (!parseRes.success) {
        console.warn('error parsing row for upsert in local list, using as is', {
          error: zx.fmtError(parseRes.error),
          schema: fmtSchema(s),
          cfg: toUpsertRaw
        });
        return toUpsertRaw;
      }
      return parseRes.data;
    }) ?? toUpsertRaw;
    const newData = isPastUpdate || isSameValues ? data : match(existingIndex).with(-1, () => concat(data, toUpsert)).otherwise(existingIndex => {
      const newData = [...data];
      newData[existingIndex] = toUpsert;
      return newData;
    });
    if (debugName) consoleDebug('listMods.upsert', debugName, {
      isSameValues,
      toUpsertRaw,
      newData,
      isPastUpdate,
      item,
      newRow,
      updatedAt: hasUpdatedAt(item) ? DateTime.fromISO(item.updated_at).toISO() : undefined,
      updatedAtNew: hasUpdatedAt(newRow) ? DateTime.fromISO(newRow.updated_at).toISO() : undefined
    });
    return newData;
  };
  const get = (id: string) => data?.find(d => d.id == id);
  return {
    del,
    upsert,
    get
  };
};
export type ListMods<T extends {
  id: string;
}> = ReturnType<typeof listMods<T>>;
export type UIStateItemProps<ZCfg extends ZodSchema<any>> = {
  state: UIState;
  subPath?: string[];
  schema: ZCfg;
  validator?: (v: z.infer<ZCfg>) => FormErrors;
  type?: string;
  disabled?: boolean;
  onUpdate?: (props: OnUpdateProps<z.infer<ZCfg>>) => void;
  onChange?: (props: OnChangeProps<z.infer<ZCfg>>) => void;
  debounceSave?: number;
  debugName?: string;
  /** set to be the value if there is nothing stored. Set to null if you want values to be null rather than the default from the schema */
  initial?: z.infer<ZCfg> | null;
};
export type UIItemReturn<ZCfg extends ZodSchema<any>, Initial extends z.infer<ZCfg> | null = z.infer<ZCfg>> = {
  values: Initial extends null ? z.infer<ZCfg> | null : z.infer<ZCfg>;
  getInputProps: ReturnType<typeof useDbForm<z.infer<ZCfg>>>['getInputProps'];
  isDirtyUIState: boolean;
  isDirtyVsInitial: boolean;
  setFieldValue: ReturnType<typeof useDbForm<z.infer<ZCfg>>>['setFieldValue'];
  setValues: ReturnType<typeof useDbForm<z.infer<ZCfg>>>['setValues'];
  validate: ReturnType<typeof useDbForm<z.infer<ZCfg>>>['validate'];
  setFieldValueDynamic: ReturnType<typeof useDbForm<z.infer<ZCfg>>>['setFieldValueDynamic'];
  del: () => Promise<void>;
  subset: <TSub extends PlainObject>(path: string) => ReturnType<typeof dbFormSubset<TSub>>;
  initial: Initial;
};

/** Use to bind with forms to interact with state from `useUIState` */
export const useUIItem = t0 => {
  const $ = _c(59);
  let debugName;
  let disabled;
  let onChange;
  let onUpdate;
  let props;
  let schema;
  let state;
  let subPath;
  let t1;
  let typeRaw;
  let validator;
  if ($[0] !== t0) {
    ({
      state,
      subPath,
      schema,
      validator,
      type: typeRaw,
      disabled,
      onUpdate,
      onChange,
      debounceSave: t1,
      debugName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = debugName;
    $[2] = disabled;
    $[3] = onChange;
    $[4] = onUpdate;
    $[5] = props;
    $[6] = schema;
    $[7] = state;
    $[8] = subPath;
    $[9] = t1;
    $[10] = typeRaw;
    $[11] = validator;
  } else {
    debugName = $[1];
    disabled = $[2];
    onChange = $[3];
    onUpdate = $[4];
    props = $[5];
    schema = $[6];
    state = $[7];
    subPath = $[8];
    t1 = $[9];
    typeRaw = $[10];
    validator = $[11];
  }
  const debounceSave = t1 === undefined ? 500 : t1;
  let t2;
  if ($[12] !== props.initial || $[13] !== schema) {
    t2 = props.initial ?? (props.initial === null ? null : doTo(schema.safeParse({}), _temp) as T);
    $[12] = props.initial;
    $[13] = schema;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const initial = t2;
  const type = typeRaw ?? schema.description;
  if (!type) {
    throw new Error("useUIState requires a type, either as a property or as a schema description");
  }
  const {
    crudItem
  } = state;
  let t3;
  if ($[15] !== crudItem || $[16] !== schema || $[17] !== subPath || $[18] !== type) {
    t3 = crudItem({
      schema,
      type,
      subPath
    });
    $[15] = crudItem;
    $[16] = schema;
    $[17] = subPath;
    $[18] = type;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const crud = t3;
  const {
    item
  } = crud;
  const t4 = item ? item.cfg : initial;
  let t5;
  if ($[20] !== crud || $[21] !== onUpdate) {
    t5 = props_0 => {
      crud.upsert(props_0.cfg);
      onUpdate?.(props_0);
    };
    $[20] = crud;
    $[21] = onUpdate;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== schema || $[24] !== validator) {
    t6 = validator ?? zodResolver(schema);
    $[23] = schema;
    $[24] = validator;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== debounceSave || $[27] !== debugName || $[28] !== onChange || $[29] !== schema || $[30] !== t4 || $[31] !== t5 || $[32] !== t6) {
    t7 = {
      stored: t4,
      onUpdate: t5,
      onChange,
      debounceSave,
      debugName,
      schema,
      validator: t6
    };
    $[26] = debounceSave;
    $[27] = debugName;
    $[28] = onChange;
    $[29] = schema;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  const uiForm = useDbForm(t7);
  const {
    values: valuesUI,
    getInputProps,
    setFieldValue,
    setValues,
    validate,
    setFieldValueDynamic
  } = uiForm;
  let t8;
  if ($[34] !== uiForm) {
    t8 = {
      uiForm
    };
    $[34] = uiForm;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  const latest = useLatest(t8);
  let t9;
  if ($[36] !== crud || $[37] !== latest) {
    t9 = async () => {
      const {
        uiForm: uiForm_0
      } = latest.current;
      uiForm_0.clearDirty();
      crud.del();
    };
    $[36] = crud;
    $[37] = latest;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const del = t9;
  const values = useMemoValue(disabled ? initial : valuesUI ?? initial);
  const isDirtyUIState = !disabled && uiForm.isDirty;
  let t10;
  if ($[39] !== disabled || $[40] !== initial || $[41] !== values) {
    t10 = !disabled && !deepEquals(values, initial);
    $[39] = disabled;
    $[40] = initial;
    $[41] = values;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const isDirtyVsInitial = t10;
  let t11;
  if ($[43] !== getInputProps || $[44] !== isDirtyUIState || $[45] !== isDirtyVsInitial || $[46] !== setFieldValue || $[47] !== setFieldValueDynamic || $[48] !== setValues || $[49] !== validate || $[50] !== values) {
    t11 = {
      values,
      getInputProps,
      isDirtyUIState,
      isDirtyVsInitial,
      setFieldValue,
      setValues,
      validate,
      setFieldValueDynamic
    };
    $[43] = getInputProps;
    $[44] = isDirtyUIState;
    $[45] = isDirtyVsInitial;
    $[46] = setFieldValue;
    $[47] = setFieldValueDynamic;
    $[48] = setValues;
    $[49] = validate;
    $[50] = values;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  const form = t11;
  let t12;
  if ($[52] !== uiForm) {
    t12 = path => dbFormSubset(uiForm, path);
    $[52] = uiForm;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  const subset = t12;
  const t13 = initial as any;
  let t14;
  if ($[54] !== del || $[55] !== form || $[56] !== subset || $[57] !== t13) {
    t14 = {
      ...form,
      del,
      subset,
      initial: t13
    };
    $[54] = del;
    $[55] = form;
    $[56] = subset;
    $[57] = t13;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  return t14;
};
export type UseUIItem<T extends ZodSchema<any>> = ReturnType<typeof useUIItem<T>>;
function _temp(o) {
  return o.success ? o.data : undefined;
}